/**
 * OTAジョブ管理ページ
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { otaApi, firmwareApi, deviceApi, groupApi } from '../services/api';
import { Icon } from '../components/icons';
import { Button, Card, Alert, Input, Select, Modal } from '../components/ui';

const STATUS_LABELS = {
  IN_PROGRESS: { label: '実行中', color: 'bg-blue-100 text-blue-700' },
  QUEUED: { label: '待機中', color: 'bg-yellow-100 text-yellow-700' },
  COMPLETED: { label: '完了', color: 'bg-green-100 text-green-700' },
  CANCELED: { label: 'キャンセル', color: 'bg-slate-100 text-slate-600' },
  DELETION_IN_PROGRESS: { label: '削除中', color: 'bg-red-100 text-red-700' },
};

export default function OTAJobs() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  // 新規ジョブ作成
  const [showCreate, setShowCreate] = useState(false);
  const [firmware, setFirmware] = useState([]);
  const [groups, setGroups] = useState([]);
  const [devices, setDevices] = useState([]);
  const [creating, setCreating] = useState(false);

  // フォームデータ
  const [formData, setFormData] = useState({
    firmwareVersion: '',
    targetType: 'group', // group or device
    targetGroups: [],
    targetDevices: [],
    description: '',
    targetSelection: 'SNAPSHOT',
  });

  // データ取得
  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const data = await otaApi.list(params);
      setJobs(data.jobs || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchFormData = useCallback(async () => {
    try {
      const [fwData, groupData, deviceData] = await Promise.all([
        firmwareApi.list(),
        groupApi.list(),
        deviceApi.list({ maxResults: 100 }),
      ]);

      // リリース済 or テスト中のみ
      setFirmware((fwData.firmware || []).filter(
        (fw) => fw.status === 'released' || fw.status === 'testing'
      ));
      setGroups(groupData.groups || []);
      setDevices(deviceData.devices || []);
    } catch (err) {
      console.warn('Form data fetch error:', err.message);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  useEffect(() => {
    if (showCreate) {
      fetchFormData();
    }
  }, [showCreate, fetchFormData]);

  // ジョブ作成
  const handleCreate = async (e) => {
    e.preventDefault();

    if (!formData.firmwareVersion) {
      alert('ファームウェアバージョンを選択してください');
      return;
    }

    let targets = [];
    if (formData.targetType === 'group') {
      if (formData.targetGroups.length === 0) {
        alert('対象グループを選択してください');
        return;
      }
      // グループARNに変換
      targets = formData.targetGroups.map(
        (g) => `arn:aws:iot:ap-northeast-1:996561439165:thinggroup/${g}`
      );
    } else {
      if (formData.targetDevices.length === 0) {
        alert('対象デバイスを選択してください');
        return;
      }
      // ThingARNに変換
      targets = formData.targetDevices.map(
        (d) => `arn:aws:iot:ap-northeast-1:996561439165:thing/${d}`
      );
    }

    setCreating(true);
    try {
      const result = await otaApi.create({
        firmwareVersion: formData.firmwareVersion,
        targets,
        targetSelection: formData.targetSelection,
        description: formData.description || undefined,
      });

      setShowCreate(false);
      setFormData({
        firmwareVersion: '',
        targetType: 'group',
        targetGroups: [],
        targetDevices: [],
        description: '',
        targetSelection: 'SNAPSHOT',
      });
      await fetchJobs();

      // 詳細ページへ遷移
      navigate(`/ota/${result.jobId}`);
    } catch (err) {
      alert('ジョブ作成エラー: ' + err.message);
    } finally {
      setCreating(false);
    }
  };

  // グループ/デバイス選択ハンドラー
  const toggleGroup = (groupName) => {
    setFormData((prev) => ({
      ...prev,
      targetGroups: prev.targetGroups.includes(groupName)
        ? prev.targetGroups.filter((g) => g !== groupName)
        : [...prev.targetGroups, groupName],
    }));
  };

  const toggleDevice = (thingName) => {
    setFormData((prev) => ({
      ...prev,
      targetDevices: prev.targetDevices.includes(thingName)
        ? prev.targetDevices.filter((d) => d !== thingName)
        : [...prev.targetDevices, thingName],
    }));
  };

  const formatDate = (isoStr) => {
    if (!isoStr) return '-';
    return new Date(isoStr).toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-slate-500">ファームウェアOTA配信ジョブの管理</p>

      {error && <Alert variant="error" onClose={() => setError(null)}>{error}</Alert>}

      {/* フィルター */}
      <div className="flex items-center gap-4">
        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          options={[
            { value: '', label: 'すべてのステータス' },
            { value: 'IN_PROGRESS', label: '実行中' },
            { value: 'COMPLETED', label: '完了' },
            { value: 'CANCELED', label: 'キャンセル' },
          ]}
        />
        <Button variant="secondary" onClick={() => fetchJobs()}>
          <Icon name="refresh" className="w-4 h-4" />
          更新
        </Button>
        <div className="flex-1" />
        <Button variant="primary" onClick={() => setShowCreate(true)}>
          <Icon name="plus" className="w-4 h-4" />
          新規ジョブ作成
        </Button>
      </div>

      {/* ジョブ一覧 */}
      <Card padding="p-0">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            ジョブなし
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">ジョブID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">FWバージョン</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">ステータス</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">作成日時</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {jobs.map((job) => (
                <tr
                  key={job.jobId}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/ota/${job.jobId}`)}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{job.jobId}</div>
                    {job.description && (
                      <div className="text-xs text-slate-500 truncate max-w-xs">{job.description}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-700">
                    v{job.firmwareVersion}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded-full ${STATUS_LABELS[job.status]?.color || 'bg-slate-100'}`}>
                      {STATUS_LABELS[job.status]?.label || job.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-500">
                    {formatDate(job.createdAt)}
                  </td>
                  <td className="px-4 py-3">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/ota/${job.jobId}`);
                      }}
                    >
                      詳細
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* 新規ジョブ作成モーダル */}
      <Modal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        title="新規OTAジョブ作成"
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowCreate(false)}>
              キャンセル
            </Button>
            <Button onClick={handleCreate} loading={creating}>
              ジョブ作成
            </Button>
          </>
        }
      >
        <form onSubmit={handleCreate} className="space-y-6">
          {/* ファームウェア選択 */}
          <div>
            <Select
              label="ファームウェアバージョン"
              required
              value={formData.firmwareVersion}
              onChange={(e) => setFormData({ ...formData, firmwareVersion: e.target.value })}
              options={[
                { value: '', label: '選択してください' },
                ...firmware.map((fw) => ({
                  value: fw.version,
                  label: `v${fw.version} (${fw.status})`,
                })),
              ]}
            />
            {firmware.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">
                テスト中またはリリース済のファームウェアがありません
              </p>
            )}
          </div>

          {/* 対象選択タイプ */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">配信対象</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="targetType"
                  value="group"
                  checked={formData.targetType === 'group'}
                  onChange={(e) => setFormData({ ...formData, targetType: e.target.value })}
                  className="text-primary-500 focus:ring-primary-500"
                />
                <span className="text-sm text-slate-700">グループで指定</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="targetType"
                  value="device"
                  checked={formData.targetType === 'device'}
                  onChange={(e) => setFormData({ ...formData, targetType: e.target.value })}
                  className="text-primary-500 focus:ring-primary-500"
                />
                <span className="text-sm text-slate-700">デバイスで指定</span>
              </label>
            </div>
          </div>

          {/* グループ選択 */}
          {formData.targetType === 'group' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                対象グループ ({formData.targetGroups.length}件選択中)
              </label>
              <div className="border border-slate-200 rounded-lg max-h-48 overflow-y-auto">
                {groups.length === 0 ? (
                  <div className="p-4 text-sm text-slate-500 text-center">
                    グループがありません
                  </div>
                ) : (
                  groups.map((g) => (
                    <label
                      key={g.groupName}
                      className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 cursor-pointer border-b border-slate-100 last:border-0"
                    >
                      <input
                        type="checkbox"
                        checked={formData.targetGroups.includes(g.groupName)}
                        onChange={() => toggleGroup(g.groupName)}
                        className="text-primary-500 focus:ring-primary-500 rounded"
                      />
                      <Icon name="folder" className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-700">{g.groupName}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}

          {/* デバイス選択 */}
          {formData.targetType === 'device' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                対象デバイス ({formData.targetDevices.length}件選択中)
              </label>
              <div className="border border-slate-200 rounded-lg max-h-48 overflow-y-auto">
                {devices.length === 0 ? (
                  <div className="p-4 text-sm text-slate-500 text-center">
                    デバイスがありません
                  </div>
                ) : (
                  devices.map((d) => (
                    <label
                      key={d.thingName}
                      className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 cursor-pointer border-b border-slate-100 last:border-0"
                    >
                      <input
                        type="checkbox"
                        checked={formData.targetDevices.includes(d.thingName)}
                        onChange={() => toggleDevice(d.thingName)}
                        className="text-primary-500 focus:ring-primary-500 rounded"
                      />
                      <Icon name="drone" className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-700">{d.thingName}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}

          {/* ジョブ設定 */}
          <div>
            <Select
              label="ターゲット選択タイプ"
              value={formData.targetSelection}
              onChange={(e) => setFormData({ ...formData, targetSelection: e.target.value })}
              options={[
                { value: 'SNAPSHOT', label: 'スナップショット（現在のデバイスのみ）' },
                { value: 'CONTINUOUS', label: '継続的（新規追加デバイスも含む）' },
              ]}
            />
            <p className="text-xs text-slate-500 mt-1">
              スナップショット: ジョブ作成時のデバイスのみ対象<br />
              継続的: グループに後から追加されたデバイスも対象
            </p>
          </div>

          {/* 説明 */}
          <div>
            <Input
              label="説明（任意）"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="例: 本番環境への v1.2.0 配信"
            />
          </div>
        </form>
      </Modal>
    </div>
  );
}
